import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';

const BillingSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [billingType, setBillingType] = useState('private');
  const [formData, setFormData] = useState({
    // Common fields
    street: '',
    postal_code: '',
    city: '',
    country: 'DE',
    // Private fields
    full_name: '',
    // Business fields
    company_name: '',
    vat_id: '',
    contact_person: '',
    internal_reference: ''
  });

  useEffect(() => {
    loadBillingProfile();
  }, []);

  const loadBillingProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/billing/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setBillingType(data.billing_type || 'private');
        setFormData({
          street: data.street || '',
          postal_code: data.postal_code || '',
          city: data.city || '',
          country: data.country || 'DE',
          full_name: data.full_name || '',
          company_name: data.company_name || '',
          vat_id: data.vat_id || '',
          contact_person: data.contact_person || '',
          internal_reference: data.internal_reference || ''
        });
      }
    } catch (error) {
      console.error('Error loading billing profile:', error);
      toast.error('Fehler beim Laden der Rechnungsdaten');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    // Validation
    if (billingType === 'private' && !formData.full_name) {
      toast.error('Bitte gib deinen vollständigen Namen ein');
      setSaving(false);
      return;
    }

    if (billingType === 'business' && !formData.company_name) {
      toast.error('Bitte gib den Firmennamen ein');
      setSaving(false);
      return;
    }

    if (!formData.street || !formData.postal_code || !formData.city) {
      toast.error('Bitte gib eine vollständige Adresse ein');
      setSaving(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/billing/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          billing_type: billingType,
          ...formData
        })
      });

      if (response.ok) {
        toast.success('Rechnungsdaten erfolgreich gespeichert');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Speichern');
      }
    } catch (error) {
      console.error('Error saving billing profile:', error);
      toast.error('Fehler beim Speichern der Rechnungsdaten');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2B2142]"></div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Rechnungsdaten</h2>
        <p className="text-gray-600 mb-6">
          Diese Daten werden für Rechnungen und Abrechnungen verwendet.
        </p>

        <form onSubmit={handleSubmit}>
          {/* Billing Type Selection */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Ich kaufe als:
            </label>
            <div className="flex gap-4">
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="billing_type"
                  value="private"
                  checked={billingType === 'private'}
                  onChange={(e) => setBillingType(e.target.value)}
                  className="w-4 h-4 text-[#2B2142] focus:ring-[#2B2142]"
                />
                <span className="ml-2 text-gray-700">Privatperson</span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="billing_type"
                  value="business"
                  checked={billingType === 'business'}
                  onChange={(e) => setBillingType(e.target.value)}
                  className="w-4 h-4 text-[#2B2142] focus:ring-[#2B2142]"
                />
                <span className="ml-2 text-gray-700">Unternehmen</span>
              </label>
            </div>
          </div>

          {/* Private Customer Fields */}
          {billingType === 'private' && (
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Vollständiger Name *
              </label>
              <input
                type="text"
                name="full_name"
                value={formData.full_name}
                onChange={handleInputChange}
                placeholder="Max Mustermann"
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2B2142] focus:border-transparent"
              />
            </div>
          )}

          {/* Business Customer Fields */}
          {billingType === 'business' && (
            <>
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Firmenname (inkl. Rechtsform) *
                </label>
                <input
                  type="text"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleInputChange}
                  placeholder="z.B. Eventservice Müller GmbH"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2B2142] focus:border-transparent"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Bitte Rechtsform im Namen angeben (z.B. GmbH, UG, e.K.)
                </p>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Umsatzsteuer-ID (USt-IdNr.)
                </label>
                <input
                  type="text"
                  name="vat_id"
                  value={formData.vat_id}
                  onChange={handleInputChange}
                  placeholder="DE123456789"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2B2142] focus:border-transparent"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Optional, aber empfohlen für steuerliche Zwecke
                </p>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Ansprechpartner
                </label>
                <input
                  type="text"
                  name="contact_person"
                  value={formData.contact_person}
                  onChange={handleInputChange}
                  placeholder="z.B. Max Müller (Geschäftsführer)"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2B2142] focus:border-transparent"
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Interne Referenz / Bestellnummer
                </label>
                <input
                  type="text"
                  name="internal_reference"
                  value={formData.internal_reference}
                  onChange={handleInputChange}
                  placeholder="Optional"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2B2142] focus:border-transparent"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Wird auf der Rechnung angezeigt (z.B. für Buchhaltung)
                </p>
              </div>
            </>
          )}

          {/* Address Fields (Common) */}
          <div className="border-t border-gray-200 pt-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Rechnungsadresse</h3>
            
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Straße und Hausnummer *
              </label>
              <input
                type="text"
                name="street"
                value={formData.street}
                onChange={handleInputChange}
                placeholder="Musterstraße 123"
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2B2142] focus:border-transparent"
              />
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Postleitzahl *
                </label>
                <input
                  type="text"
                  name="postal_code"
                  value={formData.postal_code}
                  onChange={handleInputChange}
                  placeholder="12345"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2B2142] focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Stadt *
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  placeholder="Berlin"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2B2142] focus:border-transparent"
                />
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Land
              </label>
              <select
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2B2142] focus:border-transparent"
              >
                <option value="DE">Deutschland</option>
                <option value="AT">Österreich</option>
                <option value="CH">Schweiz</option>
              </select>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-[#FFF0D1]/30 border border-[#2B2142]/20 rounded-lg p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-[#2B2142]/60" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-[#2B2142]">
                  <strong>Hinweis:</strong> Änderungen gelten nur für zukünftige Rechnungen. 
                  Bereits ausgestellte Rechnungen bleiben unverändert.
                </p>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-3 bg-gradient-to-r from-[#2B2142] to-[#1E1730] text-white font-semibold rounded-lg hover:from-[#1E1730] hover:to-[#2B2142] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {saving ? 'Wird gespeichert...' : 'Rechnungsdaten speichern'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BillingSettings;
